/**
 * Search bar mounted in the AppBar on `/:user/find`. All behavior lives in
 * `useFindStore`; this component is presentational + reads
 * `filteredStatements.length` for the live counter.
 */

import * as React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, X } from 'lucide-react'
import { useFindStore, SEARCH_PHASES } from '../../stores/useFindStore'
import { useFilterStore } from '../../stores/useFilterStore'

const ICON_BUTTON_CLASS =
    'tw-flex tw-items-center tw-justify-center tw-rounded ' +
    'tw-cursor-pointer ' +
    'tw-text-neutral-400 dark:tw-text-neutral-500 ' +
    'hover:tw-text-neutral-700 dark:hover:tw-text-neutral-200 ' +
    'hover:tw-bg-neutral-100 dark:hover:tw-bg-neutral-700/60 ' +
    'tw-transition-colors'

const Chip = ({ term, onRemove }) => (
    <motion.span
        layout
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ duration: 0.15, ease: 'easeOut' }}
        className="
            tw-inline-flex tw-items-center tw-gap-1.5
            tw-px-2 tw-py-0.5
            tw-rounded-md
            tw-bg-neutral-100 dark:tw-bg-neutral-800
            tw-border tw-border-solid tw-border-neutral-200 dark:tw-border-neutral-700/80
            tw-text-xs tw-font-medium
            tw-text-neutral-700 dark:tw-text-neutral-200
            tw-shadow-sm
        "
    >
        <span
            aria-hidden="true"
            className="tw-h-2 tw-w-2 tw-rounded-full tw-flex-shrink-0"
            style={{ backgroundColor: 'var(--ai-buttons-color)' }}
        />
        <span className="tw-max-w-[140px] tw-truncate">{term}</span>
        <button
            type="button"
            onClick={e => {
                e.stopPropagation()
                onRemove(term)
            }}
            aria-label={`Remove ${term}`}
            className={
                ICON_BUTTON_CLASS +
                ' tw-h-3.5 tw-w-3.5 tw-bg-transparent tw-border-0 tw-p-0'
            }
        >
            <X className="tw-w-3 tw-h-3" />
        </button>
    </motion.span>
)

export default function SearchInput() {
    const terms = useFindStore(s => s.terms)
    const searchPhase = useFindStore(s => s.searchPhase)
    const addTerm = useFindStore(s => s.addTerm)
    const removeTerm = useFindStore(s => s.removeTerm)

    const filteredStatements = useFilterStore(s => s.filteredStatements)
    const filteredCount = filteredStatements?.length ?? 0
    const graphCount = React.useMemo(() => {
        if (!filteredStatements) return 0
        const contextIds = new Set()
        for (const statement of filteredStatements) {
            if (statement?.context != null) contextIds.add(statement.context)
        }
        return contextIds.size
    }, [filteredStatements])

    const [draft, setDraft] = React.useState('')
    const [focused, setFocused] = React.useState(false)
    const inputRef = React.useRef(null)

    const isLoading = searchPhase === SEARCH_PHASES.LOADING
    const hasChips = terms.length > 0

    const submitDraft = () => {
        const cleaned = draft.trim()
        if (!cleaned) return
        addTerm(cleaned)
        setDraft('')
    }

    const handleKeyDown = e => {
        // Stop propagation so the in-graph keyboard shortcuts don't
        // intercept letters / Backspace from the dock input.
        e.nativeEvent?.stopImmediatePropagation?.()
        if (e.key === 'Enter') {
            e.preventDefault()
            submitDraft()
        } else if (e.key === 'Backspace' && draft === '' && terms.length > 0) {
            e.preventDefault()
            removeTerm(terms[terms.length - 1])
        }
    }

    return (
        // The Tailwind config scopes `tw-*` utilities to DESCENDANTS of
        // `.project-notes-scope`. Putting `tw-*` on the same element as the
        // scope class doesn't match — inner row carries the layout.
        <div className="project-notes-scope" style={{ flex: 1, minWidth: 0 }}>
        <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0 tw-flex-nowrap">
            <div
                onClick={() => inputRef.current?.focus()}
                className={
                    'tw-flex tw-items-center tw-gap-1.5 tw-flex-1 tw-min-w-0 ' +
                    'tw-px-2.5 tw-py-1 ' +
                    'tw-rounded-xl ' +
                    'tw-border tw-border-solid ' +
                    'tw-cursor-text ' +
                    'tw-transition-all '
                }
                style={{
                    maxWidth: 480,
                    // Inherit the AppBar surface so the pill reads as part
                    // of the toolbar. `--taginput-background` would have
                    // been the natural token but it has `!important` on
                    // the light value in style.css that blocks the dark
                    // override from winning.
                    backgroundColor: 'inherit',
                    // Matches the AppBar drawer-button gray (hardcoded in
                    // AppBar/index.jsx line ~124). Translucent so it adapts
                    // to whatever surface sits behind it.
                    borderColor: focused
                        ? 'var(--ai-buttons-color)'
                        : 'rgba(100,100,100,0.7)',
                    boxShadow: focused
                        ? '0 0 0 1px rgb(var(--ai-buttons-color-rgb) / 0.25)'
                        : undefined,
                }}
            >
                <Search
                    size={16}
                    className="tw-text-neutral-400 dark:tw-text-neutral-500 tw-flex-shrink-0"
                />
                {/*
                 * `flex-nowrap` + horizontal scroll keeps the pill exactly one
                 * line tall no matter how many chips the user adds. The global
                 * style.css rule sets `::-webkit-scrollbar { height: 0px }`,
                 * so the WebKit scrollbar is invisible; `scrollbarWidth: none`
                 * does the same for Firefox.
                 */}
                <div
                    className="tw-flex tw-items-center tw-gap-1.5 tw-flex-nowrap tw-flex-1 tw-min-w-0 tw-overflow-x-auto"
                    style={{ scrollbarWidth: 'none' }}
                >
                    <AnimatePresence initial={false}>
                        {terms.map(t => (
                            <Chip key={t} term={t} onRemove={removeTerm} />
                        ))}
                    </AnimatePresence>
                    <input
                        ref={inputRef}
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onKeyUp={e =>
                            e.nativeEvent?.stopImmediatePropagation?.()
                        }
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        placeholder={
                            hasChips
                                ? 'Add another term'
                                : 'Search concepts across all graphs…'
                        }
                        aria-label="Search concepts"
                        className="
                            tw-flex-1 tw-min-w-[80px] sm:tw-min-w-[140px]
                            tw-bg-transparent tw-border-0 tw-outline-none
                            tw-text-sm
                            tw-text-neutral-700 dark:tw-text-neutral-200
                            placeholder:tw-text-neutral-400 dark:placeholder:tw-text-neutral-500
                        "
                    />
                </div>
            </div>

            {hasChips && (
                <div
                    className="
                        tw-hidden sm:tw-inline-flex tw-items-center tw-gap-1
                        tw-px-2.5 tw-py-1
                        tw-rounded-full
                        tw-border tw-border-solid
                        tw-text-xs tw-flex-shrink-0
                        tw-whitespace-nowrap
                        tw-transition-opacity
                    "
                    style={{
                        backgroundColor:
                            'rgb(var(--ai-graph-accent-rgb) / 0.12)',
                        borderColor:
                            'rgb(var(--ai-graph-accent-rgb) / 0.35)',
                        opacity: isLoading ? 0.6 : 1,
                    }}
                >
                    <span
                        className="tw-font-bold"
                        style={{ color: 'var(--ai-graph-accent)' }}
                    >
                        {isLoading
                            ? '…'
                            : `${filteredCount} ${
                                  filteredCount === 1
                                      ? 'statement'
                                      : 'statements'
                              }`}
                    </span>
                    <span className="tw-text-neutral-400 dark:tw-text-neutral-500">
                        ·
                    </span>
                    <span className="tw-text-neutral-500 dark:tw-text-neutral-400">
                        {graphCount} {graphCount === 1 ? 'graph' : 'graphs'}
                    </span>
                </div>
            )}
        </div>
        </div>
    )
}
